import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { cn, Flex, SidebarTrigger, Text } from '@kognitos/lattice';

import SidebarLogo from '@/assets/images/kognitos-logo-sidebar.svg?react';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/**
 * Top-of-rail brand row, the same height as the page header next to it. The
 * Kognitos mark sits on a square brand-colored tile as wide as the collapsed
 * rail, so it stays put when the rail expands. The collapse trigger sits at the
 * top-right and hides when collapsed — re-expansion is via hover. Only the
 * "Kognitos" label and the trigger fade out on collapse.
 *
 * The logo links to the workspace's automations, which is the "go home"
 * affordance for the rail. It falls back to a bare mark before a workspace is
 * resolved, since there is nowhere to go yet.
 */
const SidebarBrandRow = ({
  isCollapsed,
  onTriggerClick,
  isHoverOverlay,
}: {
  isCollapsed: boolean;
  onTriggerClick?: () => void;
  isHoverOverlay?: boolean;
}) => {
  const { t } = useTranslation('navigation');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const logo = <SidebarLogo />;
  const homeHref =
    currentOrganizationId && currentWorkspaceId
      ? getRoutePath('AUTOMATIONS', {
          orgId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
        })
      : undefined;

  return (
    <Flex
      align="center"
      justify="between"
      width="full"
      overflow="hidden"
      className="h-15 shrink-0 pr-2"
    >
      <Flex align="center" className="h-full min-w-0">
        <Flex
          align="center"
          justify="center"
          className={cn(
            'h-full w-15 shrink-0 border-b border-sidebar-border bg-primary text-primary-foreground',
            !isCollapsed && 'rounded-br-xs border-r',
          )}
        >
          {homeHref ? (
            <NavLink to={homeHref} aria-label={t('navigation.sidebar.brand')}>
              {logo}
            </NavLink>
          ) : (
            logo
          )}
        </Flex>
        {!isCollapsed && (
          <Text
            level="base"
            weight="medium"
            className="app-sidebar-row-label ml-3 truncate tracking-[-0.01em] text-sidebar-foreground"
          >
            {t('navigation.sidebar.brand')}
          </Text>
        )}
      </Flex>
      {!isCollapsed && (
        <SidebarTrigger
          className="h-7 w-7 shrink-0 text-sidebar-muted"
          onClick={onTriggerClick}
          toggleOnClick={!isHoverOverlay}
        />
      )}
    </Flex>
  );
};

export default SidebarBrandRow;
